import { useParams } from "react-router-dom";
import { Clock, Calendar, Layers, Star, Tv } from "lucide-react";
import { Img } from "../components/Img";
import { WatchNowButton } from "../components/WatchNowButton";
import { PlayTrailerButton } from "../components/TrailerButtons";
import { MovieRow } from "../components/MovieRow";
import { AdSlot } from "../components/AdSlot";
import { ErrorState } from "../components/ErrorState";
import { useFetch } from "../lib/useFetch";
import { FALLBACK_POSTER, backdropUrl, posterUrl, profileUrl, year } from "../lib/api";

const runtime = (m) => (m ? `${Math.floor(m / 60)}h ${m % 60}m` : "—");

const DetailSkeleton = () => (
  <div className="mx-auto flex max-w-7xl gap-10 px-5 pt-40 md:px-10">
    <div className="skeleton hidden aspect-[2/3] w-72 shrink-0 rounded-2xl md:block" />
    <div className="flex-1 space-y-5">
      <div className="skeleton h-4 w-40 rounded" />
      <div className="skeleton h-14 w-3/4 rounded" />
      <div className="skeleton h-24 w-full rounded" />
      <div className="skeleton h-14 w-56 rounded-full" />
    </div>
  </div>
);

const Cast = ({ cast }) => (
  <section className="mx-auto max-w-7xl px-5 py-10 md:px-10" data-testid="cast-section">
    <p className="eyebrow text-[#D4AF37]">On screen</p>
    <h2 className="mt-2 font-display text-2xl font-semibold text-white md:text-3xl">Top Cast</h2>
    <div className="no-scrollbar mt-6 flex gap-5 overflow-x-auto pb-2">
      {cast.map((c) => (
        <div key={c.id} data-testid={`cast-member-${c.id}`} className="w-28 shrink-0 md:w-32">
          <div className="aspect-square overflow-hidden rounded-full ring-1 ring-white/10">
            <Img src={profileUrl(c.profile_path)} fallback={FALLBACK_POSTER} alt={c.name} className="h-full w-full object-cover" />
          </div>
          <p className="mt-3 line-clamp-1 text-center text-sm font-medium text-white">{c.name}</p>
          <p className="line-clamp-1 text-center text-xs text-neutral-500">{c.character}</p>
        </div>
      ))}
    </div>
  </section>
);

const Trailer = ({ id, title }) => (
  <section className="mx-auto max-w-7xl px-5 py-10 md:px-10" data-testid="trailer-section">
    <p className="eyebrow text-[#D4AF37]">Preview</p>
    <h2 className="mt-2 font-display text-2xl font-semibold text-white md:text-3xl">Official Trailer</h2>
    <div className="mt-6 aspect-video w-full overflow-hidden rounded-2xl border border-white/10 bg-black lg:w-4/5">
      <iframe
        data-testid="trailer-iframe"
        src={`https://www.youtube-nocookie.com/embed/${id}?rel=0`}
        title={`${title} trailer`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        className="h-full w-full"
      />
    </div>
  </section>
);

const Seasons = ({ seasons }) => (
  <section className="mx-auto max-w-7xl px-5 py-10 md:px-10" data-testid="seasons-section">
    <p className="eyebrow text-[#D4AF37]">Episodes guide</p>
    <h2 className="mt-2 font-display text-2xl font-semibold text-white md:text-3xl">Seasons</h2>
    <div className="no-scrollbar mt-6 flex gap-5 overflow-x-auto pb-2">
      {seasons.map((s) => (
        <div key={s.id} data-testid={`season-${s.season_number}`} className="w-36 shrink-0 md:w-40">
          <div className="aspect-[2/3] overflow-hidden rounded-xl ring-1 ring-white/10">
            <Img src={posterUrl(s.poster_path)} fallback={FALLBACK_POSTER} alt={s.name} className="h-full w-full object-cover" />
          </div>
          <p className="mt-3 line-clamp-1 font-display text-[15px] font-semibold text-white">{s.name}</p>
          <p className="text-xs text-neutral-500">
            {s.episode_count} episodes · {year(s.air_date)}
          </p>
        </div>
      ))}
    </div>
  </section>
);

export default function MovieDetail({ media = "movie" }) {
  const { id } = useParams();
  const isTv = media === "tv";
  const { data: m, loading, error, retry } = useFetch(`/${isTv ? "tv" : "movies"}/${id}`);

  if (loading) return <DetailSkeleton />;
  if (error)
    return (
      <div className="mx-auto max-w-7xl px-5 pt-36 md:px-10">
        <ErrorState message={error} onRetry={retry} testId="detail-error" />
      </div>
    );

  return (
    <div data-testid="movie-detail-page">
      <section className="relative min-h-[88vh] w-full overflow-hidden">
        <Img src={backdropUrl(m.backdrop_path, "original")} fallback="/fallback-backdrop.svg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-black/50" />
        <div className="grain absolute inset-0" />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-10 px-5 pb-16 pt-32 md:flex-row md:items-end md:px-10 md:pt-40">
          <div className="hero-copy w-44 shrink-0 overflow-hidden rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] ring-1 ring-[#D4AF37]/30 sm:w-56 md:w-72">
            <Img src={posterUrl(m.poster_path, "w500")} fallback={FALLBACK_POSTER} alt={m.title} className="aspect-[2/3] w-full object-cover" data-testid="detail-poster" />
          </div>
          <div className="hero-copy max-w-3xl">
            <div className="flex flex-wrap gap-2" data-testid="detail-genres">
              {(m.genres || []).map((g) => (
                <span key={g.id} className="rounded-full border border-[#D4AF37]/40 px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-[#D4AF37]">{g.name}</span>
              ))}
            </div>
            <h1 data-testid="detail-title" className="mt-5 font-display text-4xl font-bold leading-[1.05] text-white sm:text-5xl lg:text-6xl">{m.title}</h1>
            {m.tagline && <p data-testid="detail-tagline" className="mt-3 font-display text-lg italic text-neutral-300">“{m.tagline}”</p>}
            <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-neutral-300">
              <span data-testid="detail-rating" className="flex items-center gap-1.5 text-[#D4AF37]"><Star className="h-4 w-4 fill-current" /> {m.vote_average?.toFixed(1)}<span className="text-neutral-500">/10</span></span>
              <span data-testid="detail-year" className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-neutral-500" /> {year(m.release_date)}</span>
              {isTv ? (
                <>
                  <span data-testid="detail-seasons-count" className="flex items-center gap-1.5"><Layers className="h-4 w-4 text-neutral-500" /> {m.number_of_seasons} {m.number_of_seasons === 1 ? "Season" : "Seasons"}</span>
                  <span data-testid="detail-episodes-count" className="flex items-center gap-1.5"><Tv className="h-4 w-4 text-neutral-500" /> {m.number_of_episodes} Episodes</span>
                </>
              ) : (
                <span data-testid="detail-runtime" className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-neutral-500" /> {runtime(m.runtime)}</span>
              )}
            </div>
            <p data-testid="detail-overview" className="mt-6 max-w-2xl text-sm leading-relaxed text-neutral-300 md:text-base">{m.overview || "No overview available."}</p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <WatchNowButton item={m} size="lg" testId="detail-watch-now-btn" className="gold-glow" />
              <PlayTrailerButton item={m} className="py-4" />
            </div>
          </div>
        </div>
      </section>

      <AdSlot name="detail_top" />
      {m.cast?.length > 0 && <Cast cast={m.cast} />}
      {isTv && m.seasons?.length > 0 && <Seasons seasons={m.seasons} />}
      {m.trailer_key && <Trailer id={m.trailer_key} title={m.title} />}
      <AdSlot name="detail_bottom" />
      {m.similar?.length > 0 && <MovieRow id="similar" title="More Like This" eyebrow="You may also enjoy" movies={m.similar} />}

      <section className="mx-auto max-w-7xl px-5 py-12 md:px-10">
        <div className="flex flex-col items-start justify-between gap-6 rounded-2xl border border-[#D4AF37]/25 bg-[#121212] p-8 md:flex-row md:items-center md:p-12">
          <div>
            <p className="eyebrow text-[#D4AF37]">Ready when you are</p>
            <h2 className="mt-2 font-display text-2xl text-white md:text-3xl">Stream {m.title} tonight.</h2>
          </div>
          <WatchNowButton item={m} size="lg" testId="detail-bottom-watch-now-btn" />
        </div>
      </section>
    </div>
  );
}
